/* BOJOURNAL — tabs.js
   <tab-set>        text tabs with keyboard support and a soft panel change
   <track-controls> ← 01 / 06 → for any horizontal .track
   Loaded by the sections that use them (featured collections, related works). */
(() => {
  const B = window.Bojournal;
  if (!B || !B.Component || customElements.get('tab-set')) return;

  /* ------------------------------------------------------------------
     Track controls
     <track-controls for="TrackId"> with [data-prev] [data-next] [data-count]
     ------------------------------------------------------------------ */
  class TrackControls extends B.Component {
    init() {
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.count = this.querySelector('[data-count]');
      this.on(this.prev, 'click', () => this.go(-1));
      this.on(this.next, 'click', () => this.go(1));
      this.bind(this.getAttribute('for'));
    }

    teardown() {
      if (this._trackController) this._trackController.abort();
      if (this._resize) this._resize.disconnect();
    }

    bind(id) {
      this.teardown();
      this.track = document.getElementById(id);
      if (!this.track) return;
      this.setAttribute('for', id);
      this._trackController = new AbortController();
      this.track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(this._raf);
          this._raf = requestAnimationFrame(() => this.sync());
        },
        { passive: true, signal: this._trackController.signal }
      );
      if ('ResizeObserver' in window) {
        this._resize = new ResizeObserver(() => this.sync());
        this._resize.observe(this.track);
      }
      this.sync();
    }

    step() {
      const item = this.track.firstElementChild;
      if (!item) return 0;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return item.getBoundingClientRect().width + gap;
    }

    go(direction) {
      if (!this.track) return;
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      this.track.scrollBy({
        left: this.step() * direction * (rtl ? -1 : 1),
        behavior: B.reduceMotion.matches ? 'auto' : 'smooth'
      });
    }

    sync() {
      if (!this.track || !this.track.isConnected) return;
      const total = this.track.children.length;
      const step = this.step();
      const scrolled = Math.abs(this.track.scrollLeft);
      const max = this.track.scrollWidth - this.track.clientWidth;
      const index = step ? Math.round(scrolled / step) : 0;
      const pad = (n) => String(n).padStart(2, '0');
      if (this.count) this.count.textContent = `${pad(Math.min(index + 1, total))} / ${pad(total)}`;
      if (this.prev) this.prev.disabled = scrolled <= 2;
      if (this.next) this.next.disabled = scrolled >= max - 2;
      this.hidden = max <= 2;
    }
  }

  B.define('track-controls', TrackControls);

  /* ------------------------------------------------------------------
     Tabs — arrow keys (mirrored in RTL), Home, End.
     The panel change is an opacity + short lift. The wrapper keeps its
     height during the swap, so the page below never jumps.
     ------------------------------------------------------------------ */
  class TabSet extends B.Component {
    init() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.tabs.forEach((tab, i) => {
        this.on(tab, 'click', () => this.select(i));
        this.on(tab, 'keydown', (event) => this.onKey(event, i));
      });
      // Theme Editor: selecting a tab's block shows its panel.
      this.on(document, 'shopify:block:select', (event) => {
        const index = this.tabs.findIndex((tab) => tab === event.target || tab.contains(event.target));
        if (index > -1) this.select(index, false, true);
      });
    }

    onKey(event, i) {
      const rtl = getComputedStyle(this).direction === 'rtl';
      const nextKey = rtl ? 'ArrowLeft' : 'ArrowRight';
      const prevKey = rtl ? 'ArrowRight' : 'ArrowLeft';
      const n = this.tabs.length;
      let target = null;
      if (event.key === nextKey) target = (i + 1) % n;
      if (event.key === prevKey) target = (i - 1 + n) % n;
      if (event.key === 'Home') target = 0;
      if (event.key === 'End') target = n - 1;
      if (target === null) return;
      event.preventDefault();
      this.select(target, true);
    }

    panelFor(tab) {
      return document.getElementById(tab.getAttribute('aria-controls'));
    }

    select(index, focus, instant) {
      const nextTab = this.tabs[index];
      const current = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
      if (focus) nextTab.focus();
      if (nextTab === current) return;

      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
        tab.tabIndex = i === index ? 0 : -1;
      });
      // Keep the chosen tab in view when the tab row scrolls (mobile).
      nextTab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: instant ? 'auto' : 'smooth' });

      const from = current ? this.panelFor(current) : null;
      const to = this.panelFor(nextTab);
      if (!to) return;
      const wrapper = to.parentElement;
      const out = instant ? 0 : B.duration('--dur-1');

      clearTimeout(this._swap);
      clearTimeout(this._settle);
      wrapper.style.minHeight = `${wrapper.offsetHeight}px`;
      if (from) from.classList.add('is-leaving');

      this._swap = setTimeout(() => {
        this.tabs.forEach((tab) => {
          const panel = this.panelFor(tab);
          if (!panel) return;
          panel.hidden = panel !== to;
          panel.classList.remove('is-leaving', 'is-entering');
        });
        to.classList.add('is-entering');
        B.reveal(to);
        const controls = this.closest('section') && this.closest('section').querySelector('track-controls');
        const track = to.querySelector('.track');
        if (controls && controls.bind && track) {
          track.scrollLeft = 0;
          controls.bind(track.id);
        } else if (controls) {
          controls.hidden = true; // an empty world has nothing to page through
        }
        requestAnimationFrame(() => requestAnimationFrame(() => to.classList.remove('is-entering')));
        this._settle = setTimeout(() => (wrapper.style.minHeight = ''), B.duration('--dur-3') + 40);
      }, out);
    }
  }

  B.define('tab-set', TabSet);
})();
